/**
 * Validating and inspecting a user-supplied audio file.
 *
 * The sibling of lib/imageUpload.ts, and deliberately shaped like it — the accepted
 * list, the derived `accept` attribute, the human labels, the byte ceiling and a
 * discriminated rejection carrying the message a user reads. Same reasons: a component
 * that validates as well as renders ends up with the rules spread across three branches
 * of JSX, and the limits have to be assertable in a test.
 *
 * TWO THINGS ARE DIFFERENT FROM THE IMAGE CASE, and both are about audio specifically.
 *
 * 1. THE EXTENSION IS CONSULTED, which imageUpload never does. `File.type` is what the
 *    browser sniffed, and for audio it is unreliable in a way it is not for images:
 *    Firefox and several Windows builds report `''` for `.flac` and `.m4a`, and `.mp3`
 *    comes back as `audio/mpeg` on one platform and `audio/mp3` on another. Rejecting a
 *    perfectly good FLAC because the OS had no MIME entry for it is a worse failure than
 *    the one the strict check prevents. So a declared type is checked against the
 *    accepted list, and an ABSENT one falls back to the extension.
 *
 * 2. THE FIRST BYTES ARE READ, which is what makes the fallback safe. `inspectAudioFile`
 *    looks at the container's magic number — 'RIFF…WAVE', 'fLaC', 'OggS', '…ftyp', an
 *    ID3 tag or an MPEG frame sync — so a text file renamed to song.mp3 is caught even
 *    when neither its type nor its extension gave it away. This is also the only
 *    genuinely asynchronous step in the flow, which is what the panel's loading state is
 *    occupied by.
 *
 * WHAT VALIDATION IS FOR HERE
 * ---------------------------
 * Not security, exactly as in lib/imageUpload.ts. A magic number is four bytes a
 * hostile file can also start with, and none of this proves the rest of the stream
 * decodes. These checks exist so the *user* is told what went wrong instead of watching
 * nothing happen. A real backend must re-validate independently, by decoding — see
 * api/audioUploadApi.ts, which also records why nothing is stored today.
 */
import { formatBytes } from './imageUpload';

/**
 * Formats the picker accepts, as MIME types.
 *
 * Longer than the image list because the same container is announced under different
 * names by different platforms: `audio/mpeg` and `audio/mp3` are both MP3,
 * `audio/wav`/`audio/x-wav`/`audio/wave` are all WAV, and `audio/x-m4a` is what Safari
 * calls an `.m4a` that Chrome calls `audio/mp4`. All of them are the same handful of
 * formats; leaving any out rejects a file for the browser's choice of spelling.
 */
export const ACCEPTED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/flac',
  'audio/x-flac',
  'audio/ogg',
  'audio/mp4',
  'audio/x-m4a',
  'audio/aac',
] as const;

/**
 * Extensions the picker accepts, and the fallback when the browser declares no type.
 *
 * Lower case, with the dot, which is the form the `accept` attribute takes and the form
 * `extensionOf` returns — so the same list serves both without a transform between.
 */
export const ACCEPTED_AUDIO_EXTENSIONS = [
  '.mp3',
  '.wav',
  '.flac',
  '.ogg',
  '.oga',
  '.m4a',
  '.aac',
] as const;

/**
 * The `accept` attribute for the file input, derived so the picker and the code cannot
 * disagree.
 *
 * Both lists, unlike the image input which needs only MIME types: a file picker filters
 * on whichever of the two the platform can resolve, and on the platforms that report no
 * MIME type for `.flac` the extension is the only thing that keeps it selectable.
 */
export const AUDIO_ACCEPT_ATTRIBUTE = [...ACCEPTED_AUDIO_TYPES, ...ACCEPTED_AUDIO_EXTENSIONS].join(
  ','
);

/** Human list for the drop zone's helper text. */
export const ACCEPTED_AUDIO_LABEL = 'MP3, WAV, FLAC, OGG, M4A or AAC';

/**
 * Largest file accepted, in bytes.
 *
 * 50MB. A five-minute stereo WAV at 44.1kHz/16-bit is about 52MB, so this admits a
 * typical MP3, FLAC or M4A master comfortably and stops just short of a full-length
 * uncompressed WAV — which is the point at which "pick a smaller file" is better advice
 * than a read that takes ten seconds. Nothing is uploaded (see api/audioUploadApi.ts),
 * so this ceiling is about the browser's memory rather than a transfer budget; a real
 * storage service would set its own, server-side, and it would not have to match.
 */
export const MAX_AUDIO_BYTES = 50 * 1024 * 1024;

/** MAX_AUDIO_BYTES as a human string, for messages. */
export const MAX_AUDIO_LABEL = '50MB';

/**
 * Why a file was refused.
 *
 * Four cases, not three: `contents` is the one imageUpload has no equivalent of, and it
 * is the outcome of the byte check rather than of the metadata. Keeping it distinct
 * matters because the advice differs — a `type` rejection means "pick a different file",
 * a `contents` rejection means "that file says it is audio and is not".
 */
export type AudioRejection =
  | { reason: 'empty'; message: string }
  | { reason: 'type'; message: string }
  | { reason: 'size'; message: string }
  | { reason: 'contents'; message: string };

/** The lower-cased extension of a filename, with its dot, or '' if it has none. */
export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  // `dot < 1` rather than `=== -1`: a leading dot is a hidden file, not an extension,
  // so ".flac" as a whole filename has no format to read off it.
  return dot < 1 ? '' : filename.slice(dot).toLowerCase();
}

/**
 * Checks a file's metadata against the format and size rules.
 *
 * Returns null when the file's *metadata* is acceptable, which is not the same as the
 * file being audio — `inspectAudioFile` is the second half and reads the bytes. Split in
 * two because this half is synchronous and can run the moment a file is picked, while
 * the other is a read the UI has to show a loading state for.
 */
export function validateAudioFile(file: File): AudioRejection | null {
  if (file.size === 0) {
    return { reason: 'empty', message: 'That file is empty. Pick an audio file.' };
  }

  /*
    The declared type when there is one, the extension when there is not.

    Not both-or-either: a file that declares `text/plain` is rejected even if it is
    called song.mp3, so the extension can only ever admit a file the browser had no
    opinion about. That keeps the imageUpload rule — the extension is not evidence —
    while still accepting the FLAC that Firefox refuses to name.
  */
  if (file.type) {
    if (!(ACCEPTED_AUDIO_TYPES as readonly string[]).includes(file.type)) {
      return {
        reason: 'type',
        message: `That file isn't a supported audio format. Use ${ACCEPTED_AUDIO_LABEL}.`,
      };
    }
  } else if (!(ACCEPTED_AUDIO_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))) {
    return {
      reason: 'type',
      message: `That file isn't a supported audio format. Use ${ACCEPTED_AUDIO_LABEL}.`,
    };
  }

  if (file.size > MAX_AUDIO_BYTES) {
    return {
      reason: 'size',
      message: `That file is ${formatBytes(file.size)}. The limit is ${MAX_AUDIO_LABEL}.`,
    };
  }
  return null;
}

/** The containers the byte check can recognise. */
export type AudioContainer = 'mp3' | 'wav' | 'flac' | 'ogg' | 'mp4';

/** What each container is called on screen. */
export const AUDIO_CONTAINER_LABEL: Record<AudioContainer, string> = {
  mp3: 'MP3',
  wav: 'WAV',
  flac: 'FLAC',
  ogg: 'OGG',
  mp4: 'MP4 / M4A',
};

/**
 * How many bytes `inspectAudioFile` reads.
 *
 * 64 is comfortably past the furthest magic number below — MP4's `ftyp` sits at offset
 * 4, WAV's `WAVE` at 8 — and small enough that the read is instant on a 50MB file
 * because `File.slice` does not touch the rest of it.
 */
export const AUDIO_SNIFF_BYTES = 64;

/** ASCII at an offset, for the four-character container magics. */
function asciiAt(bytes: Uint8Array, offset: number, length: number): string {
  let out = '';
  for (let i = 0; i < length; i += 1) out += String.fromCharCode(bytes[offset + i] ?? 0);
  return out;
}

/**
 * Identifies the container from the head of the file, or null if it is not audio.
 *
 * Order matters. The four-character magics are exact and are checked first; MPEG comes
 * last because its frame sync is eleven set bits and nothing else, which is a pattern
 * arbitrary binary hits often enough that testing it before the exact ones would
 * mislabel real WAVs and MP4s as MP3s.
 */
export function sniffAudioContainer(bytes: Uint8Array): AudioContainer | null {
  if (asciiAt(bytes, 0, 4) === 'RIFF' && asciiAt(bytes, 8, 4) === 'WAVE') return 'wav';
  if (asciiAt(bytes, 0, 4) === 'fLaC') return 'flac';
  if (asciiAt(bytes, 0, 4) === 'OggS') return 'ogg';
  // ISO base media: a four-byte box length, then 'ftyp'. Covers .m4a and .mp4 alike.
  if (asciiAt(bytes, 4, 4) === 'ftyp') return 'mp4';
  // An ID3v2 tag ahead of the audio, which is how most MP3s in the wild start.
  if (asciiAt(bytes, 0, 3) === 'ID3') return 'mp3';
  // A bare MPEG frame sync: eleven set bits.
  if (bytes.length >= 2 && bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0) return 'mp3';
  return null;
}

/**
 * Reads the head of a file and reports which container it actually is.
 *
 * Rejects rather than returning null, matching readImageAsDataUrl: the caller turns the
 * message into something on screen either way, and a thrown error cannot be mistaken for
 * a successful inspection that happened to find nothing.
 *
 * This is the "loading state" step. It is fast, but it is genuinely asynchronous and
 * genuinely fallible — a file removed from disk between the picker and the read fails
 * here, which is the audio equivalent of imageUpload's preview-failure case.
 */
export async function inspectAudioFile(file: File): Promise<AudioContainer> {
  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.slice(0, AUDIO_SNIFF_BYTES).arrayBuffer());
  } catch {
    throw new Error('Could not read that audio file.');
  }
  const container = sniffAudioContainer(bytes);
  if (!container) {
    throw new Error(
      `That file doesn't look like audio inside. Use ${ACCEPTED_AUDIO_LABEL}.`
    );
  }
  return container;
}

/**
 * Picks the first file out of a drop.
 *
 * A drop can carry several files, a directory, or nothing. Taking the first and
 * validating it is what a single-file target should do; reporting "no audio here" beats
 * silently ignoring the gesture. The same shape as firstImageFrom, kept separate rather
 * than shared because the two would have to be generic over nothing.
 */
export function firstAudioFrom(list: FileList | null): File | null {
  if (!list || list.length === 0) return null;
  return list.item(0);
}
